/**
 * Shared App primitives — the small vocabulary every App composes.
 * Product surface (light + dark tokens), C-Mode density: cards, one primary
 * CTA per screen, plain-language empty states.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';

export function AppCard({
  children,
  className = '',
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const base =
    'rounded-xl border border-gray-200 bg-white p-5 dark:border-[var(--rule)] dark:bg-[var(--surface)]';
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`block w-full text-left ${base} hover:border-gray-300 dark:hover:bg-[var(--hover-tint)] ${className}`}
      >
        {children}
      </button>
    );
  }
  return <div className={`${base} ${className}`}>{children}</div>;
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-500 dark:text-[var(--text-ter)]">
      {children}
    </p>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

export function PrimaryButton({ className = '', ...props }: ButtonProps) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2.5 font-body text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    />
  );
}

export function SecondaryButton({ className = '', ...props }: ButtonProps) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 font-body text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-pri)] dark:hover:bg-[var(--hover-tint)] ${className}`}
    />
  );
}

export function StatusChip({
  label,
  tone = 'neutral',
}: {
  label: string;
  tone?: 'neutral' | 'green' | 'mustard' | 'coral' | 'slate';
}) {
  const tones: Record<string, string> = {
    neutral:
      'bg-gray-100 text-gray-700 dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]',
    green:
      'bg-alpine-green-50 text-alpine-green-700 dark:bg-alpine-green-900/30 dark:text-alpine-green-300',
    mustard:
      'bg-electric-mustard-50 text-electric-mustard-800 dark:bg-electric-mustard-900/30 dark:text-electric-mustard-300',
    coral:
      'bg-luminous-coral-50 text-luminous-coral-700 dark:bg-luminous-coral-900/30 dark:text-luminous-coral-300',
    slate:
      'bg-slate-blue-50 text-slate-blue-700 dark:bg-slate-blue-900/30 dark:text-slate-blue-300',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tones[tone]}`}
    >
      {label}
    </span>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-10 text-center dark:border-[var(--rule)] dark:bg-[var(--surface)]">
      <p className="font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">{title}</p>
      {hint ? (
        <p className="mx-auto mt-1 max-w-sm font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
          {hint}
        </p>
      ) : null}
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}
